/**
 * Permissions Store Module
 * Manages user seat permissions for RBAC enforcement
 */

export default {
  namespaced: true,
  state: {
    permissions: {
      dashboard: { view: true, edit: false },
      activeProjects: { view: false, edit: false },
      archivedProjects: { view: false, edit: false },
      proposals: { view: false, edit: false },
      payments: { view: false, edit: false },
      invoices: { view: false, edit: false },
      settings: {
        accounts: false,
        security: false,
        billingPlans: false,
        seats: { view: false, edit: false },
        notifications: false,
        signatures: false,
        quickbooks: false,
      },
    },
    isOwner: false,
    readOnly: false,
    loaded: false,
  },
  getters: {
    /**
     * Check if user has view permission for a tab
     */
    canView: state => tab => {
      if (state.isOwner) return true
      if (tab === 'settings') {
        // Check if user has access to any settings sub-tab
        return Object.entries(state.permissions.settings).some(([subTab, perm]) => {
          if (subTab === 'accounts' || subTab === 'security' || subTab === 'billingPlans' || subTab === 'notifications' || subTab === 'signatures' || subTab === 'quickbooks') {
            return perm === true
          }
          return perm?.view === true
        })
      }
      return state.permissions[tab]?.view || false
    },

    /**
     * Check if user has edit permission for a tab
     */
    canEdit: state => tab => {
      if (state.isOwner) return true
      if (tab === 'settings') {
        // Check if user has edit permission on any settings sub-tab
        return Object.entries(state.permissions.settings).some(([subTab, perm]) => {
          if (subTab === 'accounts' || subTab === 'security' || subTab === 'billingPlans' || subTab === 'notifications' || subTab === 'signatures' || subTab === 'quickbooks') {
            return perm === true
          }
          return perm?.edit === true
        })
      }
      return state.permissions[tab]?.edit || false
    },

    /**
     * Check if user can access a specific settings sub-tab
     */
    canViewSettingsTab: state => subTab => {
      if (state.isOwner) return true
      // account, security, billingPlans, notifications, signatures, and quickbooks are boolean access flags
      if (subTab === 'accounts' || subTab === 'security' || subTab === 'billingPlans' || subTab === 'notifications' || subTab === 'signatures' || subTab === 'quickbooks') {
        return state.permissions.settings[subTab] === true
      }
      return state.permissions.settings[subTab]?.view || false
    },

    canEditSettingsTab: state => subTab => {
      if (state.isOwner) return true
      // account, security, billingPlans, notifications, signatures, and quickbooks are boolean access flags (can't differentiate edit)
      if (subTab === 'accounts' || subTab === 'security' || subTab === 'billingPlans' || subTab === 'notifications' || subTab === 'signatures' || subTab === 'quickbooks') {
        return state.permissions.settings[subTab] === true
      }
      return state.permissions.settings[subTab]?.edit || false
    },

    /**
     * Get all accessible tabs
     */
    accessibleTabs: state => {
      if (state.isOwner) {
        return ['dashboard', 'activeProjects', 'archivedProjects', 'proposals', 'payments', 'invoices', 'settings']
      }

      const tabs = []
      if (state.permissions.dashboard?.view) tabs.push('dashboard')
      if (state.permissions.activeProjects?.view) tabs.push('activeProjects')
      if (state.permissions.archivedProjects?.view) tabs.push('archivedProjects')
      if (state.permissions.proposals?.view) tabs.push('proposals')
      if (state.permissions.payments?.view) tabs.push('payments')
      if (state.permissions.invoices?.view) tabs.push('invoices')

      // Add settings if any sub-tab is accessible
      const hasSettingsAccess = Object.entries(state.permissions.settings).some(([subTab, perm]) => {
        // account, security, billingPlans, notifications, signatures, and quickbooks are boolean permissions
        if (subTab === 'accounts' || subTab === 'security' || subTab === 'billingPlans' || subTab === 'notifications' || subTab === 'signatures' || subTab === 'quickbooks') {
          return perm === true
        }
        return perm?.view === true
      })
      if (hasSettingsAccess) tabs.push('settings')

      return tabs
    },

    /**
     * Get accessible settings sub-tabs
     */
    accessibleSettingsTabs: state => {
      if (state.isOwner) {
        return ['accounts', 'security', 'billingPlans', 'seats', 'notifications', 'signatures', 'quickbooks']
      }

      return Object.keys(state.permissions.settings).filter(tab => {
        // account, security, billingPlans, notifications, signatures, and quickbooks are boolean permissions
        if (tab === 'accounts' || tab === 'security' || tab === 'billingPlans' || tab === 'notifications' || tab === 'signatures' || tab === 'quickbooks') {
          return state.permissions.settings[tab] === true
        }
        return state.permissions.settings[tab]?.view === true
      })
    },

    isPermissionsLoaded: state => state.loaded,
    isOwnerUser: state => state.isOwner,
  },
  mutations: {
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions || state.permissions
    },

    SET_OWNER_STATUS(state, isOwner) {
      state.isOwner = isOwner
    },

    SET_READ_ONLY(state, readOnly) {
      state.readOnly = readOnly
    },

    SET_LOADED(state, loaded) {
      state.loaded = loaded
    },

    RESET_PERMISSIONS(state) {
      state.permissions = {
        dashboard: { view: true, edit: false },
        activeProjects: { view: false, edit: false },
        archivedProjects: { view: false, edit: false },
        proposals: { view: false, edit: false },
        payments: { view: false, edit: false },
        invoices: { view: false, edit: false },
        settings: {
          accounts: false,
          security: false,
          billingPlans: false,
          seats: { view: false, edit: false },
          notifications: false,
          signatures: false,
          quickbooks: false,
        },
      }
      state.isOwner = false
      state.readOnly = false
      state.loaded = false
    },
  },
  actions: {
    /**
     * Initialize permissions from API
     */
    async initializePermissions({ commit }, seatId = null) {
      try {
        commit('SET_LOADED', false)

        // If no seatId, user is owner (workspace owner or admin)
        if (!seatId) {
          commit('SET_OWNER_STATUS', true)
          commit('SET_PERMISSIONS', {
            dashboard: { view: true, edit: true },
            activeProjects: { view: true, edit: true },
            archivedProjects: { view: true, edit: true },
            proposals: { view: true, edit: true },
            payments: { view: true, edit: true },
            invoices: { view: true, edit: true },
            settings: {
              accounts: true,
              security: true,
              billingPlans: { view: true, edit: true },
              seats: { view: true, edit: true },
              notifications: { view: true, edit: true },
              signatures: { view: true, edit: true },
              quickbooks: { view: true, edit: true },
            },
          })
          commit('SET_READ_ONLY', true)
          commit('SET_LOADED', true)
          return
        }

        // Fetch seat permissions from API
        // NOTE: This will be called after login when seat permissions are available
        commit('SET_LOADED', true)
      } catch (error) {
        console.error('Failed to initialize permissions:', error)
        commit('SET_LOADED', true)
      }
    },

    /**
     * Update permissions from API response
     */
    updatePermissionsFromAPI({ commit }, permissionsData) {
      try {
        if (!permissionsData) return

        // Extract permissions
        const permissions = {
          dashboard: permissionsData.dashboard || { view: true, edit: false },
          activeProjects: permissionsData.activeProjects || { view: false, edit: false },
          archivedProjects: permissionsData.archivedProjects || { view: false, edit: false },
          proposals: permissionsData.proposals || { view: false, edit: false },
          payments: permissionsData.payments || { view: false, edit: false },
          invoices: permissionsData.invoices || { view: false, edit: false },
          settings: {
            accounts: permissionsData.settings?.accounts !== undefined ? permissionsData.settings.accounts : false,
            security: permissionsData.settings?.security !== undefined ? permissionsData.settings.security : false,
            billingPlans: permissionsData.settings?.billingPlans !== undefined ? permissionsData.settings.billingPlans : false,
            seats: permissionsData.settings?.seats || { view: false, edit: false },
            notifications: permissionsData.settings?.notifications !== undefined ? permissionsData.settings.notifications : false,
            signatures: permissionsData.settings?.signatures !== undefined ? permissionsData.settings.signatures : false,
            quickbooks: permissionsData.settings?.quickbooks !== undefined ? permissionsData.settings.quickbooks : false,
          },
        }

        commit('SET_PERMISSIONS', permissions)
        commit('SET_OWNER_STATUS', permissionsData.isOwner || false)
        commit('SET_READ_ONLY', permissionsData.readOnly || false)
        commit('SET_LOADED', true)
      } catch (error) {
        console.error('Failed to update permissions:', error)
      }
    },

    /**
     * Reset permissions (on logout)
     */
    resetPermissions({ commit }) {
      commit('RESET_PERMISSIONS')
    },
  },
}
